import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

interface GoogleCredentialResponse {
  credential: string;
}

interface GoogleIdentityApi {
  initialize(options: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select: boolean;
    ux_mode: 'popup';
    context: 'signin';
  }): void;
  renderButton(
    element: HTMLElement,
    options: {
      type: 'standard';
      theme: 'outline';
      size: 'large';
      text: 'continue_with';
      shape: 'rectangular';
      locale: 'es';
      width: number;
    },
  ): void;
  prompt(): void;
  cancel(): void;
  disableAutoSelect(): void;
}

type GoogleWindow = Window & {
  google?: { accounts?: { id?: GoogleIdentityApi } };
};

/** GIS has no dependency on HTTP or user state, so logout can safely use it. */
@Injectable({ providedIn: 'root' })
export class GoogleIdentityService {
  private readonly logoutKey = 'google-one-tap-signed-out';
  private scriptLoading?: Promise<void>;
  private clientId?: string;
  private credentialHandler?: (credential: string) => void;
  private signedOut = false;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  private get api(): GoogleIdentityApi | undefined {
    return (this.document.defaultView as GoogleWindow | null)?.google?.accounts?.id;
  }

  load(): Promise<void> {
    if (this.api) return Promise.resolve();
    if (this.scriptLoading) return this.scriptLoading;

    this.scriptLoading = new Promise<void>((resolve, reject) => {
      const script = this.document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;

      const finish = (error?: Error) => {
        clearTimeout(timer);
        script.onload = null;
        script.onerror = null;
        if (error) {
          script.remove();
          reject(error);
        } else {
          resolve();
        }
      };
      const timer = setTimeout(
        () => finish(new Error('Google Identity Services did not load in time.')),
        15000,
      );
      script.onload = () =>
        finish(this.api ? undefined : new Error('Google Identity Services is unavailable.'));
      script.onerror = () => finish(new Error('Google Identity Services could not be loaded.'));
      this.document.head.appendChild(script);
    }).catch(error => {
      this.scriptLoading = undefined;
      throw error;
    });

    return this.scriptLoading;
  }

  render(clientId: string, element: HTMLElement, onCredential: (credential: string) => void): void {
    const api = this.api;
    if (!api) throw new Error('Google Identity Services is unavailable.');

    this.credentialHandler = onCredential;
    if (this.clientId !== clientId) {
      api.initialize({
        client_id: clientId,
        callback: response => this.credentialHandler?.(response.credential),
        auto_select: false,
        ux_mode: 'popup',
        context: 'signin',
      });
      this.clientId = clientId;
    }

    api.renderButton(element, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      locale: 'es',
      width: Math.min(400, element.clientWidth || 300),
    });
  }

  prompt(): void {
    if (this.wasSignedOut()) return;
    try {
      this.api?.prompt();
    } catch {
      // Browsers can block One Tap independently of the explicit Google button.
    }
  }

  cancelPrompt(): void {
    this.api?.cancel();
  }

  release(): void {
    this.credentialHandler = undefined;
    this.cancelPrompt();
  }

  prepareForLogout(): void {
    this.signedOut = true;
    try {
      this.document.defaultView?.sessionStorage.setItem(this.logoutKey, '1');
    } catch {
      // The in-memory flag also works when browser storage is unavailable.
    }
    this.api?.disableAutoSelect();
    this.release();
  }

  completeSignIn(): void {
    this.signedOut = false;
    try {
      this.document.defaultView?.sessionStorage.removeItem(this.logoutKey);
    } catch {
      // Storage restrictions must not interrupt a successful sign-in.
    }
    this.cancelPrompt();
  }

  private wasSignedOut(): boolean {
    try {
      return (
        this.signedOut || this.document.defaultView?.sessionStorage.getItem(this.logoutKey) === '1'
      );
    } catch {
      return this.signedOut;
    }
  }
}
